/**
 * The tracked-account query hooks: the campaign's account list, registering one, one account's
 * tracking configuration, and the campaign's burn rate.
 *
 * Every campaign-addressed route here takes the campaign id alone: `api` derives the tenant from
 * the campaign through the caller's memberships, so no `tenant_id` is sent.
 */

import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiFailureError } from '../../../../lib/http/errors';
import { useApiClient } from '../../../session/client';
import {
  newAccountEstimateSchema,
  tagCategorySchema,
  tagValueSchema,
  trackedAccountLabelSchema,
  trackedAccountRowSchema,
  trackingWriteSchema,
  type AccountKpis,
  type AccountPublications,
  type AccountReachSeries,
  type AccountSentiment,
  type AccountTopics,
  type BurnRate,
  type NewAccountEstimate,
  type TagCategory,
  type TagValue,
  type TrackedAccountLabel,
  type TrackedAccountLabelWrite,
  type TrackedAccountRegistration,
  type TrackedAccountRow,
  type Tracking,
  type TrackingConfigWrite,
  type TrackingWrite,
} from './schemas';
import { GET_ROUTES, routePath } from '../../../../lib/http/routes';

/** ISO instants, half-open `[from, to)` -- matching the monitoring area's own `Window`, mirrored
 *  here rather than imported: the two areas' windows are unrelated concepts that happen to share
 *  a shape (`[one-grammar-across-repos]` binds one producer's payload, not two callers' params). */
export interface ReadingWindow {
  from: string;
  to: string;
}

function readingWindowParams(window: ReadingWindow): URLSearchParams {
  return new URLSearchParams({ from: window.from, to: window.to });
}

export interface AccountFilter {
  tag?: string;
  q?: string;
  team_id?: string;
}

export const ACCOUNTS_ROOT_QUERY_KEY = (campaignId: string) =>
  ['campaign', campaignId, 'accounts'] as const;

export const ACCOUNTS_QUERY_KEY = (campaignId: string, filter: AccountFilter = {}) =>
  [...ACCOUNTS_ROOT_QUERY_KEY(campaignId), filter] as const;
export const ACCOUNT_TRACKING_QUERY_KEY = (campaignId: string, trackedAccountId: string) =>
  ['campaign', campaignId, 'account-tracking', trackedAccountId] as const;
export const ACCOUNT_TAGS_QUERY_KEY = (campaignId: string) =>
  ['campaign', campaignId, 'account-tags'] as const;
export const CAMPAIGN_BURN_RATE_QUERY_KEY = (campaignId: string) =>
  ['campaign', campaignId, 'burn-rate'] as const;

function filterParams(filter: AccountFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.tag) params.set('tag', filter.tag);
  if (filter.q) params.set('q', filter.q);
  if (filter.team_id) params.set('team_id', filter.team_id);
  return params;
}

/** What a saved configuration or a new account changes: the list's estimate and overrides flag,
 *  the campaign's burn rate, and the tag picker. */
function invalidateAccountViews(queryClient: QueryClient, campaignId: string): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ACCOUNTS_ROOT_QUERY_KEY(campaignId) }),
    queryClient.invalidateQueries({ queryKey: CAMPAIGN_BURN_RATE_QUERY_KEY(campaignId) }),
    queryClient.invalidateQueries({ queryKey: ACCOUNT_TAGS_QUERY_KEY(campaignId) }),
  ]);
}

export function useTrackedAccounts(
  campaignId: string,
  filter: AccountFilter = {},
): UseQueryResult<TrackedAccountRow[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<TrackedAccountRow[], ApiFailureError>({
    queryKey: ACCOUNTS_QUERY_KEY(campaignId, filter),
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.trackedAccounts, { campaignId }, filterParams(filter)),
        GET_ROUTES.trackedAccounts.schema,
      );
      return items;
    },
    enabled: campaignId !== '',
  });
}

export function useRegisterAccount(
  campaignId: string,
): UseMutationResult<TrackedAccountRow, ApiFailureError, TrackedAccountRegistration> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<TrackedAccountRow, ApiFailureError, TrackedAccountRegistration>({
    mutationFn: (body) =>
      client.postParsed(`/campaigns/${campaignId}/tracked-accounts`, body, trackedAccountRowSchema),
    onSuccess: () => invalidateAccountViews(queryClient, campaignId),
  });
}

export function useAccountTracking(
  campaignId: string,
  trackedAccountId: string,
): UseQueryResult<Tracking, ApiFailureError> {
  const client = useApiClient();
  return useQuery<Tracking, ApiFailureError>({
    queryKey: ACCOUNT_TRACKING_QUERY_KEY(campaignId, trackedAccountId),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.trackedAccountTracking, { campaignId, trackedAccountId }),
        GET_ROUTES.trackedAccountTracking.schema,
      ),
    enabled: campaignId !== '' && trackedAccountId !== '',
  });
}

export function useWriteAccountTracking(
  campaignId: string,
  trackedAccountId: string,
): UseMutationResult<TrackingWrite, ApiFailureError, TrackingConfigWrite> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<TrackingWrite, ApiFailureError, TrackingConfigWrite>({
    mutationFn: (body) =>
      client.putParsed(
        `/campaigns/${campaignId}/tracked-accounts/${trackedAccountId}/tracking`,
        body,
        trackingWriteSchema,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ACCOUNT_TRACKING_QUERY_KEY(campaignId, trackedAccountId),
      });
      await invalidateAccountViews(queryClient, campaignId);
    },
  });
}

export function useRenameAccount(
  campaignId: string,
  trackedAccountId: string,
): UseMutationResult<TrackedAccountLabel, ApiFailureError, TrackedAccountLabelWrite> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<TrackedAccountLabel, ApiFailureError, TrackedAccountLabelWrite>({
    mutationFn: (body) =>
      client.putParsed(
        `/campaigns/${campaignId}/tracked-accounts/${trackedAccountId}/label`,
        body,
        trackedAccountLabelSchema,
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ACCOUNTS_ROOT_QUERY_KEY(campaignId) }),
        queryClient.invalidateQueries({
          queryKey: ACCOUNT_TRACKING_QUERY_KEY(campaignId, trackedAccountId),
        }),
      ]),
  });
}

export function useRemoveAccount(
  campaignId: string,
  trackedAccountId: string,
): UseMutationResult<void, ApiFailureError, void> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiFailureError, void>({
    mutationFn: () => client.del(`/campaigns/${campaignId}/tracked-accounts/${trackedAccountId}`),
    // Not awaited: the caller leaves the removed account's page as soon as this resolves, and
    // awaiting the refetch would first render that page's not-found state under the dialog.
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: ACCOUNT_TRACKING_QUERY_KEY(campaignId, trackedAccountId),
      });
      void queryClient.invalidateQueries({ queryKey: ACCOUNTS_ROOT_QUERY_KEY(campaignId) });
    },
  });
}

export function useAccountTags(campaignId: string): UseQueryResult<string[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<string[], ApiFailureError>({
    queryKey: ACCOUNT_TAGS_QUERY_KEY(campaignId),
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.campaignTags, { campaignId }),
        GET_ROUTES.campaignTags.schema,
      );
      return items;
    },
    enabled: campaignId !== '',
  });
}

const TAG_CATEGORIES_QUERY_KEY = (campaignId: string) =>
  ['campaign', campaignId, 'tag-categories'] as const;

/** `TagField`'s key step, and its value step: a category nests its own values (`api`'s
 *  `TagCategoryOut`), so one read serves both -- there is no separate value-list route.
 *  `active_only=true` is the picker read: a deactivated category, or a deactivated value, is
 *  left out, matching `read_vocabulary`'s own doc comment on `api`. */
export function useTagCategories(
  campaignId: string,
  enabled: boolean,
): UseQueryResult<TagCategory[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<TagCategory[], ApiFailureError>({
    queryKey: TAG_CATEGORIES_QUERY_KEY(campaignId),
    queryFn: async () => {
      const { categories } = await client.getParsed(
        routePath(GET_ROUTES.campaignTagCategories, { campaignId }, { active_only: 'true' }),
        GET_ROUTES.campaignTagCategories.schema,
      );
      return categories;
    },
    enabled,
  });
}

export function useCreateTagCategory(
  campaignId: string,
): UseMutationResult<TagCategory, ApiFailureError, { name: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<TagCategory, ApiFailureError, { name: string }>({
    mutationFn: (body) =>
      client.postParsed(`/campaigns/${campaignId}/tags/categories`, body, tagCategorySchema),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: TAG_CATEGORIES_QUERY_KEY(campaignId) }),
  });
}

export function useCreateTagValue(
  campaignId: string,
): UseMutationResult<TagValue, ApiFailureError, { categoryId: string; value: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<TagValue, ApiFailureError, { categoryId: string; value: string }>({
    mutationFn: ({ categoryId, value }) =>
      client.postParsed(
        `/campaigns/${campaignId}/tags/categories/${categoryId}/values`,
        { name: value },
        tagValueSchema,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: TAG_CATEGORIES_QUERY_KEY(campaignId) }),
  });
}

export function useCampaignBurnRate(campaignId: string): UseQueryResult<BurnRate, ApiFailureError> {
  const client = useApiClient();
  return useQuery<BurnRate, ApiFailureError>({
    queryKey: CAMPAIGN_BURN_RATE_QUERY_KEY(campaignId),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.campaignBurnRate, { campaignId }),
        GET_ROUTES.campaignBurnRate.schema,
      ),
    enabled: campaignId !== '',
  });
}

/** `POST .../tracked-accounts/estimate`'s body (`NewAccountEstimateIn`): the chosen profile, or
 *  `{}` (prices zero) while none is chosen. */
export type NewAccountEstimateBody = { profile_id: string } | Record<string, never>;

/**
 * The add-account dialog footer's live preview (86:2). No
 * `placeholderData`: a profile switch clears the figure back
 * to pending rather than showing the old price while the new one is in flight.
 */
export function useNewAccountEstimate(
  campaignId: string,
  body: NewAccountEstimateBody,
): UseQueryResult<NewAccountEstimate, ApiFailureError> {
  const client = useApiClient();
  return useQuery<NewAccountEstimate, ApiFailureError>({
    queryKey: ['campaign', campaignId, 'new-account-estimate', JSON.stringify(body)],
    queryFn: () =>
      client.postParsed(
        `/campaigns/${campaignId}/tracked-accounts/estimate`,
        body,
        newAccountEstimateSchema,
      ),
    enabled: campaignId !== '',
  });
}

/** The account screen's five reading routes (75:2). Each takes an explicit window rather than
 *  computing "now" itself, matching `campaign/monitoring/api/queries.ts`'s own reasoning: a story
 *  or a test pins one, and every section on the page agrees on what "now" means. */
export const ACCOUNT_KPIS_QUERY_KEY = (
  campaignId: string,
  trackedAccountId: string,
  window: ReadingWindow,
) => ['campaign', campaignId, 'account-reading', trackedAccountId, 'kpis', window] as const;
export const ACCOUNT_REACH_SERIES_QUERY_KEY = (
  campaignId: string,
  trackedAccountId: string,
  window: ReadingWindow,
) => ['campaign', campaignId, 'account-reading', trackedAccountId, 'reach-series', window] as const;
export const ACCOUNT_SENTIMENT_QUERY_KEY = (
  campaignId: string,
  trackedAccountId: string,
  window: ReadingWindow,
) => ['campaign', campaignId, 'account-reading', trackedAccountId, 'sentiment', window] as const;
export const ACCOUNT_TOPICS_QUERY_KEY = (
  campaignId: string,
  trackedAccountId: string,
  window: ReadingWindow,
) => ['campaign', campaignId, 'account-reading', trackedAccountId, 'topics', window] as const;
export const ACCOUNT_PUBLICATIONS_QUERY_KEY = (
  campaignId: string,
  trackedAccountId: string,
  window: ReadingWindow,
) => ['campaign', campaignId, 'account-reading', trackedAccountId, 'publications', window] as const;

export function useAccountKpis(
  campaignId: string,
  trackedAccountId: string,
  window: ReadingWindow,
): UseQueryResult<AccountKpis, ApiFailureError> {
  const client = useApiClient();
  return useQuery<AccountKpis, ApiFailureError>({
    queryKey: ACCOUNT_KPIS_QUERY_KEY(campaignId, trackedAccountId, window),
    queryFn: () =>
      client.getParsed(
        routePath(
          GET_ROUTES.accountKpis,
          { campaignId, trackedAccountId },
          readingWindowParams(window),
        ),
        GET_ROUTES.accountKpis.schema,
      ),
    enabled: campaignId !== '' && trackedAccountId !== '',
  });
}

export function useAccountReachSeries(
  campaignId: string,
  trackedAccountId: string,
  window: ReadingWindow,
): UseQueryResult<AccountReachSeries, ApiFailureError> {
  const client = useApiClient();
  return useQuery<AccountReachSeries, ApiFailureError>({
    queryKey: ACCOUNT_REACH_SERIES_QUERY_KEY(campaignId, trackedAccountId, window),
    queryFn: () =>
      client.getParsed(
        routePath(
          GET_ROUTES.accountReachSeries,
          { campaignId, trackedAccountId },
          readingWindowParams(window),
        ),
        GET_ROUTES.accountReachSeries.schema,
      ),
    enabled: campaignId !== '' && trackedAccountId !== '',
  });
}

export function useAccountSentiment(
  campaignId: string,
  trackedAccountId: string,
  window: ReadingWindow,
): UseQueryResult<AccountSentiment, ApiFailureError> {
  const client = useApiClient();
  return useQuery<AccountSentiment, ApiFailureError>({
    queryKey: ACCOUNT_SENTIMENT_QUERY_KEY(campaignId, trackedAccountId, window),
    queryFn: () =>
      client.getParsed(
        routePath(
          GET_ROUTES.accountSentiment,
          { campaignId, trackedAccountId },
          readingWindowParams(window),
        ),
        GET_ROUTES.accountSentiment.schema,
      ),
    enabled: campaignId !== '' && trackedAccountId !== '',
  });
}

export function useAccountTopics(
  campaignId: string,
  trackedAccountId: string,
  window: ReadingWindow,
): UseQueryResult<AccountTopics, ApiFailureError> {
  const client = useApiClient();
  return useQuery<AccountTopics, ApiFailureError>({
    queryKey: ACCOUNT_TOPICS_QUERY_KEY(campaignId, trackedAccountId, window),
    queryFn: () =>
      client.getParsed(
        routePath(
          GET_ROUTES.accountTopics,
          { campaignId, trackedAccountId },
          readingWindowParams(window),
        ),
        GET_ROUTES.accountTopics.schema,
      ),
    enabled: campaignId !== '' && trackedAccountId !== '',
  });
}

export function useAccountPublications(
  campaignId: string,
  trackedAccountId: string,
  window: ReadingWindow,
): UseQueryResult<AccountPublications, ApiFailureError> {
  const client = useApiClient();
  return useQuery<AccountPublications, ApiFailureError>({
    queryKey: ACCOUNT_PUBLICATIONS_QUERY_KEY(campaignId, trackedAccountId, window),
    queryFn: () =>
      client.getParsed(
        routePath(
          GET_ROUTES.accountPublications,
          { campaignId, trackedAccountId },
          readingWindowParams(window),
        ),
        GET_ROUTES.accountPublications.schema,
      ),
    enabled: campaignId !== '' && trackedAccountId !== '',
  });
}

/** `PUT .../tracked-accounts/{id}/alignment`: moves the account to another value of the
 *  campaign's alignment category; its fixed key moves with it. */
export function useSetAlignment(
  campaignId: string,
  trackedAccountId: string,
): UseMutationResult<{ value_id: string }, ApiFailureError, { value_id: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<{ value_id: string }, ApiFailureError, { value_id: string }>({
    mutationFn: (body) =>
      client.putParsed(
        `/campaigns/${campaignId}/tracked-accounts/${trackedAccountId}/alignment`,
        body,
        z.object({ value_id: z.string() }),
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ACCOUNTS_ROOT_QUERY_KEY(campaignId) }),
        queryClient.invalidateQueries({ queryKey: ['campaign', campaignId, 'tags'] }),
      ]),
  });
}
